import { NavLink, Navigate, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, Inbox, Images, Settings as SettingsIcon, LogOut, ExternalLink, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { LOGO_MARK_URL } from "@/data/content";

const NAV = [
    { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/admin/leads", label: "Inquiries", icon: Inbox },
    { to: "/admin/portfolio", label: "Portfolio", icon: Images },
    { to: "/admin/settings", label: "Settings", icon: SettingsIcon },
];

export default function AdminLayout() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    if (user === null) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-hive-bg" data-testid="admin-loading">
                <Loader2 className="h-8 w-8 animate-spin text-violet-400" />
            </div>
        );
    }
    if (user === false) return <Navigate to="/admin/login" replace />;

    return (
        <div className="flex min-h-screen bg-hive-bg" data-testid="admin-dashboard">
            <aside className="hidden w-60 shrink-0 flex-col border-r border-white/10 bg-[#0C101A] p-5 md:flex">
                <div className="flex items-center gap-3">
                    <img src={LOGO_MARK_URL} alt="" className="h-11 w-11 rounded-xl object-cover ring-1 ring-white/20" />
                    <div className="leading-tight">
                        <p className="font-display text-sm font-bold text-white">Fusion Hives</p>
                        <p className="font-mono2 text-[9px] uppercase tracking-[0.2em] text-amber-400">Admin</p>
                    </div>
                </div>
                <nav className="mt-8 flex flex-col gap-1" aria-label="Admin">
                    {NAV.map((n) => (
                        <NavLink key={n.to} to={n.to} end={n.end} data-testid={`admin-nav-${n.label.toLowerCase()}`}
                            className={({ isActive }) => `flex items-center gap-3 rounded-xl px-4 py-2.5 text-sm font-medium transition-colors ${isActive ? "bg-violet-600/20 text-violet-200 border border-violet-500/30" : "text-slate-400 hover:bg-white/5 hover:text-white border border-transparent"}`}>
                            <n.icon className="h-4 w-4" /> {n.label}
                        </NavLink>
                    ))}
                </nav>
                <div className="mt-auto space-y-1">
                    <a href="/" target="_blank" rel="noopener noreferrer" data-testid="admin-view-site-link"
                        className="flex items-center gap-3 rounded-xl px-4 py-2.5 text-sm font-medium text-slate-400 hover:bg-white/5 hover:text-white">
                        <ExternalLink className="h-4 w-4" /> View Site
                    </a>
                    <button onClick={async () => { await logout(); navigate("/admin/login"); }} data-testid="admin-logout-button"
                        className="flex w-full items-center gap-3 rounded-xl px-4 py-2.5 text-sm font-medium text-slate-400 hover:bg-rose-500/10 hover:text-rose-300">
                        <LogOut className="h-4 w-4" /> Sign Out
                    </button>
                </div>
            </aside>
            <div className="flex min-w-0 flex-1 flex-col">
                <div className="flex items-center justify-between gap-3 border-b border-white/10 bg-[#0C101A] px-5 py-3 md:hidden">
                    <span className="font-display text-sm font-bold text-white">Fusion Hives Admin</span>
                    <div className="flex gap-1">
                        {NAV.map((n) => (
                            <NavLink key={n.to} to={n.to} end={n.end} aria-label={n.label}
                                className={({ isActive }) => `rounded-lg p-2 ${isActive ? "bg-violet-600/25 text-violet-200" : "text-slate-400"}`}>
                                <n.icon className="h-4 w-4" />
                            </NavLink>
                        ))}
                        <button onClick={async () => { await logout(); navigate("/admin/login"); }} aria-label="Sign out" className="rounded-lg p-2 text-slate-400">
                            <LogOut className="h-4 w-4" />
                        </button>
                    </div>
                </div>
                <main className="flex-1 overflow-y-auto p-5 sm:p-8">
                    <Outlet />
                </main>
            </div>
        </div>
    );
}
